import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { line } from 'd3-shape';
import { scaleTime, scaleLinear } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { round, max, maxBy, ceil, isFinite } from 'lodash';
import { usePrevious } from 'react-use';

import type { Swell } from 'types/swell';
import nextHighest from 'utils/nextHighest';
import { isTimestampThreeHourly } from 'utils/time';

import splitSwellLines from '../splitSwellLines';
import type { ForecastGraphAdvancedSwellContainerProps } from '../components/ForecastGraphAdvancedSwellContainer/ForecastGraphAdvancedSwellContainer';

import styles from '../components/ForecastGraphAdvancedSwellContainer/ForecastGraphAdvancedSwellContainer.module.scss';

const cx = classNames.bind(styles);

const getDynamicClassNames = (
  className: string,
  swellIndex: number,
  activeSwell: number,
  hoveredSwell: number,
) =>
  cx({
    [className]: true,
    [`${className}-${swellIndex}`]: true,
    [`${className}Line${swellIndex}`]: className === 'swell',
    [`${className}Line${swellIndex}Active`]:
      className === 'swell' &&
      (activeSwell === swellIndex || (hoveredSwell === swellIndex && activeSwell !== swellIndex)),
  });

const useSetupAdvancedSwellGraph = ({
  activeItem,
  activeSwell,
  data,
  graphHeight,
  graphWidth,
  hoveredSwell,
  isEnergy,
  numberOfTicks,
  onHoveredSwell,
  onSelectSwell,
  overallMaxHeight,
  setTooltipTopPosition,
  units,
}: ForecastGraphAdvancedSwellContainerProps & {
  graphHeight?: number;
  graphWidth?: number;
}) => {
  const [topSwellIndex, setTopSwellIndex] = useState(0);
  const previousActiveSwellIndex = usePrevious(activeSwell);
  const previousHoveredSwellIndex = usePrevious(hoveredSwell);

  const overallMaxEnergy = useMemo(() => {
    const maxEnergy =
      max(data.map((item) => maxBy(item.swells, (swell) => swell.power)?.power)) || 1;
    if (maxEnergy <= 300) return 300;
    if (maxEnergy > 300 && maxEnergy <= 1000) return nextHighest(maxEnergy, 100);
    if (maxEnergy > 1000 && maxEnergy <= 5000) return nextHighest(maxEnergy, 500);
    if (maxEnergy > 5000) return nextHighest(maxEnergy, 1000);
    return maxEnergy;
  }, [data]);

  useEffect(() => {
    setTopSwellIndex(activeSwell);
  }, [activeSwell]);

  useEffect(() => {
    setTopSwellIndex(hoveredSwell);
  }, [hoveredSwell]);

  useEffect(() => {
    [...new Array(6)].forEach((_, index) => {
      const swellLine = select(`.swell-${index}`);
      swellLine.attr('class', getDynamicClassNames('swell', index, activeSwell, hoveredSwell));

      if (index === topSwellIndex) {
        swellLine.raise();
      }
    });
  }, [activeSwell, hoveredSwell, previousActiveSwellIndex, topSwellIndex]);

  useEffect(() => {
    if (activeSwell !== previousActiveSwellIndex || hoveredSwell !== previousHoveredSwellIndex) {
      selectAll(`.node-${previousActiveSwellIndex}`).attr(
        'class',
        getDynamicClassNames(
          'node',
          previousActiveSwellIndex ?? activeSwell,
          activeSwell,
          hoveredSwell,
        ),
      );

      // set the new active swell nodes as active
      selectAll(`.node-${topSwellIndex}`).attr(
        'class',
        getDynamicClassNames('node', topSwellIndex, activeSwell, hoveredSwell),
      );
    }

    // Find the node and get its top position
    if (activeItem) {
      const activeNode = selectAll(`.node-${hoveredSwell}`).filter(
        (d: any) => d?.timestamp === activeItem.timestamp && d?.utcOffset === activeItem.utcOffset,
      );
      if (!activeNode?.empty()) {
        const transform = activeNode?.attr('transform');
        const topValue = transform.split(',')?.[1]?.split(')');
        if (topValue?.[0]) {
          const newTopValue = parseInt(topValue[0], 10) - 50;
          if (newTopValue <= 4) {
            // force the tooltip below the line to avoid it going outside of the graph top boundary
            setTooltipTopPosition(newTopValue + 80);
          } else {
            setTooltipTopPosition(newTopValue);
          }
        }
      }
    }
  }, [
    activeItem,
    activeSwell,
    hoveredSwell,
    previousActiveSwellIndex,
    previousHoveredSwellIndex,
    setTooltipTopPosition,
    topSwellIndex,
  ]);

  const setupAdvancedSwellGraph = useCallback(() => {
    if (graphHeight && graphWidth && isFinite(graphHeight) && isFinite(graphWidth)) {
      select(`.advanced-swell-graph-ticks`).remove();
      select(`.advanced-swell-graph-svg`).remove();

      const arrowScale: any = scaleLinear().domain([5, 20]).range([0.6, 1.3]).clamp(true);

      const graphData: Array<Array<{ hour: number; scale: number } & Swell>> = [...Array(6)].map(
        (_, swellIndex) =>
          data.map((dataPoint, index: number) => ({
            ...dataPoint,
            hour: index,
            scale: arrowScale(dataPoint.swells[swellIndex].period),
            ...dataPoint.swells[swellIndex],
          })),
      );

      const xPadding = graphWidth / (2 * data.length);
      const yPadding = 4;

      const x = scaleTime()
        .range([xPadding, graphWidth - xPadding])
        .domain([0, data.length - 1]);

      const SWELL_MAX = units === 'M' ? 0.91 : 3;

      const yMax = ceil(
        Math.max(isEnergy ? overallMaxEnergy : nextHighest(overallMaxHeight, 5), SWELL_MAX || 0),
      );

      const y = scaleLinear().range([graphHeight, yPadding]).domain([0, yMax]);

      const path = line()
        .x((d: any) => x(d.hour))
        .y((d: any) => y(isEnergy ? d.power : d.height));

      const canvas = select(`.advanced-swell-graph-canvas`);

      if (numberOfTicks > 0) {
        const intermediateTicks = [...Array(numberOfTicks - 1)].map((_, index) =>
          round((index + 1) * (yMax / numberOfTicks), 1),
        );

        const tickData = [...intermediateTicks, yMax];

        const ticks = canvas
          .append('div')
          .attr('class', cx(['advanced-swell-graph-ticks', styles.ticks]));

        ticks
          .selectAll('.advanced-swell-graph-tick')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['advanced-swell-graph-tick', styles.tick]))
          .style('top', (d) => `${y(d)}px`)
          .text(
            (d) => `${d < 10 ? round(d, 1) : round(d, 0)}${isEnergy ? 'kJ' : units?.toLowerCase()}`,
          );

        ticks
          .selectAll('.advanced-swell-graph-dashed-line')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['advanced-swell-graph-dashed-line', styles.dashedLine]))
          .style('top', (d) => `${y(d)}px`);
      }

      const svg = canvas.append('svg').attr('class', cx(['advanced-swell-graph-svg', styles.svg]));

      graphData.forEach((swell, swellIndex) => {
        const swellPoints: any = swell.filter(
          ({ height, period }: { height: number; period: number }) => height > 0 && period > 0,
        );
        const swellLines = splitSwellLines(swellPoints);
        const swellGroup = svg
          .append('g')
          .attr('class', getDynamicClassNames('swell', swellIndex, activeSwell, hoveredSwell));

        swellGroup.on('mouseover', () => onHoveredSwell(swellIndex));
        swellGroup.on('click', () => {
          onSelectSwell(swellIndex);
        });
        swellLines.forEach((swellLine) => {
          swellGroup
            .append('path')
            .attr('class', styles.visibleLine)
            .attr('d', path(swellLine as any));

          swellGroup
            .append('path')
            .attr('class', styles.invisibleLine)
            .attr('d', path(swellLine as any));
        });

        const nodeGroup = swellGroup
          .selectAll('.advanced-swell-graph-node')
          .data(swellPoints)
          .enter()
          .append('g')
          .attr('class', () => getDynamicClassNames('node', swellIndex, activeSwell, hoveredSwell))
          .attr('transform', (d: any) => {
            const scaledCenter = arrowScale(d.period) * 8;
            const circleX = x(d.hour) - scaledCenter;
            const circleY = y(isEnergy ? d.power : d.height) - scaledCenter;

            return `translate(${circleX}, ${circleY})`;
          });

        nodeGroup
          .filter((d: any) => isTimestampThreeHourly(d.timestamp, d.utcOffset))
          .append('g')
          .attr('transform', (d: any) => {
            const scaledCenter = d.scale * 8;
            return `rotate(${d.direction + 180} ${scaledCenter} ${scaledCenter}) scale(${d.scale})`;
          })
          .append('svg')
          .attr('width', '18')
          .attr('height', '18')
          .attr('viewBox', '-2 2 18 18')
          .append('path')
          .attr(
            'd',
            'M11.1225 15.3675L6 0L0.877485 15.3675C0.445798 16.6626 1.40974 18 2.77485 18H9.22515C10.5903 18 11.5542 16.6626 11.1225 15.3675Z',
          )

          .append('path')
          .attr(
            'd',
            'M11.1225 15.3675L6 0L0.877485 15.3675C0.445798 16.6626 1.40974 18 2.77485 18H9.22515C10.5903 18 11.5542 16.6626 11.1225 15.3675Z',
          )

          .attr('stroke', 'white')
          .attr('stroke-width', '0.769231')
          .attr('class', styles.inactiveNode);

        nodeGroup
          .filter((d: any) => !isTimestampThreeHourly(d.timestamp, d.utcOffset))
          .append('circle')
          .attr('vector-effect', 'non-scaling-stroke')
          .attr('cx', (d: any) => d.scale * 8)
          .attr('cy', (d: any) => d.scale * 8)
          .attr('r', 2);
      });
    }
    // Important: don't include `activeSwell` or `hoveredSwell` here or it will cause the entire graph to re-render on ever swell line hover
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    graphHeight,
    graphWidth,
    isEnergy,
    numberOfTicks,
    onHoveredSwell,
    onSelectSwell,
    overallMaxHeight,
    overallMaxEnergy,
    units,
  ]);
  return { setupAdvancedSwellGraph };
};

export default useSetupAdvancedSwellGraph;
